import React, { useEffect } from "react";

import {
  Paper,
  Typography,
  IconButton,
  CardContent,
  CardActions,
  Button,
  Input,
  TextField,
} from "@material-ui/core";

import { useForm, Controller } from "react-hook-form";

import Autocomplete from "@material-ui/lab/Autocomplete";

import CloseIcon from "@material-ui/icons/Close";

import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  /*
   * ==========================================================
   * DROPDOWN
   * ==========================================================
   */
  wrapper: {
    position: "relative",
    width: "100%",
  },

  advanceCard: {
    position: "absolute",

    /*
     * Search trigger is 42px high.
     * Place card immediately underneath it.
     */
    top: "calc(100% + 1px)",

    /*
     * Center relative to search trigger.
     */
    left: "50%",
    transform: "translateX(-50%)",

    width: 450,

    backgroundColor: "#fff",

    /*
     * Must appear above table/header.
     */
    zIndex: 99999,

    borderRadius: 4,

    boxShadow: "0 3px 10px rgba(0,0,0,0.25)",

    overflow: "visible",

    [theme.breakpoints.down("sm")]: {
      width: "calc(100vw - 32px)",
      maxWidth: 450,
    },
  },

  /*
   * CLOSE BUTTON
   */
  closeContainer: {
    display: "flex",

    justifyContent: "flex-end",
    alignItems: "center",

    height: 40,

    padding: "0 8px",

    boxSizing: "border-box",
  },

  closeIcon: {
    color: "#777",
    padding: 5,
  },

  /*
   * FORM
   */
  fieldsContainer: {
    padding: "0 24px 4px 24px",
  },

  fieldRow: {
    display: "grid",

    gridTemplateColumns: "96px 1fr",

    columnGap: 0,

    alignItems: "center",

    minHeight: 52,
  },

  fieldLabel: {
    color: "#757575",

    fontSize: 14,

    fontWeight: 600,
  },

  field: {
    width: "100%",
  },

  /*
   * INPUT / AUTOCOMPLETE
   */
  autocompleteInput: {
    width: "100%",

    "& .MuiInputBase-root": {
      paddingRight: "30px !important",
    },

    "& .MuiInputBase-input": {
      fontSize: 16,

      padding: "6px 0 !important",
    },
  },

  /*
   * BUTTONS
   */
  actions: {
    display: "flex",

    justifyContent: "flex-end",

    alignItems: "center",

    padding: "8px 10px 10px",
  },

  clearButton: {
    color: "#333",

    marginRight: 5,
  },

  searchButton: {
    backgroundColor: "#3f51b5",

    color: "#fff",

    fontWeight: 600,

    minWidth: 73,

    "&:hover": {
      backgroundColor: "#303f9f",
    },
  },
}));

const AdvanceSearch = (props) => {
  const {
    isOpen,
    handleToggle,

    clearAdvancedSearchHandler,
    searchAdvancedHandler,

    isClients,
    isInterpreters,

    handleInputChange,
    handleInterpreterInputChange,

    isSearchObj,
  } = props;

  const classes = useStyles();

  /*
   * ==========================================================
   * DEFAULT VALUES
   * ==========================================================
   */
  const formValue = {
    vatNo: isSearchObj?.vatNo || "",

    businessName: isSearchObj?.businessName || "",

    interpreter: isSearchObj?.interpreter || null,

    company: isSearchObj?.company || null,
  };

  const { register, handleSubmit, control, reset } = useForm({
    mode: "onChange",

    defaultValues: formValue,
  });

  /*
   * ==========================================================
   * KEEP FORM IN SYNC
   * ==========================================================
   */
  useEffect(() => {
    reset({
      vatNo: isSearchObj?.vatNo || "",

      businessName: isSearchObj?.businessName || "",

      interpreter: isSearchObj?.interpreter || null,

      company: isSearchObj?.company || null,
    });
  }, [
    isSearchObj?.vatNo,
    isSearchObj?.businessName,
    isSearchObj?.interpreter,
    isSearchObj?.company,
    reset,
  ]);

  /*
   * ==========================================================
   * SUBMIT
   * ==========================================================
   */
  const onSubmit = (data) => {
    searchAdvancedHandler(data);
  };

  /*
   * ==========================================================
   * CLEAR
   * ==========================================================
   */
  const clearFieldsHandler = () => {
    reset({
      vatNo: "",
      businessName: "",
      interpreter: null,
      company: null,
    });

    clearAdvancedSearchHandler();
  };

  /*
   * ==========================================================
   * CLOSED
   * ==========================================================
   */
  if (!isOpen) {
    return null;
  }

  /*
   * ==========================================================
   * PANEL
   * ==========================================================
   */
  return (
    <div className={classes.wrapper}>
      <Paper className={classes.advanceCard} elevation={5}>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          {/* =================================================
              CLOSE
          ================================================= */}
          <div className={classes.closeContainer}>
            <IconButton
              className={classes.closeIcon}
              size="small"
              onClick={handleToggle}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <CardContent className={classes.fieldsContainer}>
            {/* =================================================
                VAT NO
            ================================================= */}
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>VAT No</Typography>

              <Input
                fullWidth
                placeholder="Enter VAT No"
                name="vatNo"
                inputRef={register}
                className={classes.field}
              />
            </div>

            {/* =================================================
                BUSINESS NAME
            ================================================= */}
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>
                Business Name
              </Typography>

              <Input
                fullWidth
                placeholder="Enter Business Name"
                name="businessName"
                inputRef={register}
                className={classes.field}
              />
            </div>

            {/* =================================================
                INTERPRETER
            ================================================= */}
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>
                Interpreter
              </Typography>

              <Controller
                name="interpreter"
                control={control}
                defaultValue={null}
                render={({ value, onChange }) => (
                  <Autocomplete
                    options={isInterpreters || []}
                    value={value || null}
                    onChange={(event, newValue) => {
                      onChange(newValue);
                    }}
                    getOptionSelected={(option, selected) =>
                      option?._id === selected?._id
                    }
                    getOptionLabel={(option) =>
                      option
                        ? `${option.fullname || ""}${
                            option.int_id ? ` (${option.int_id})` : ""
                          }`
                        : ""
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Choose an interpreter"
                        variant="standard"
                        className={classes.autocompleteInput}
                        onChange={handleInterpreterInputChange}
                      />
                    )}
                    renderOption={(option) => (
                      <span>
                        {option.fullname}

                        {option.int_id ? ` (${option.int_id})` : ""}
                      </span>
                    )}
                  />
                )}
              />
            </div>

            {/* =================================================
                COMPANY
            ================================================= */}
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>Company</Typography>

              <Controller
                name="company"
                control={control}
                defaultValue={null}
                render={({ value, onChange }) => (
                  <Autocomplete
                    options={isClients || []}
                    value={value || null}
                    onChange={(event, newValue) => {
                      onChange(newValue);
                    }}
                    getOptionSelected={(option, selected) =>
                      option?._id === selected?._id
                    }
                    getOptionLabel={(option) => option?.companyName || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Choose a company"
                        variant="standard"
                        className={classes.autocompleteInput}
                        onChange={handleInputChange}
                      />
                    )}
                  />
                )}
              />
            </div>
          </CardContent>

          {/* =================================================
              ACTIONS
          ================================================= */}
          <CardActions className={classes.actions}>
            <Button
              size="small"
              type="button"
              className={classes.clearButton}
              onClick={clearFieldsHandler}
            >
              CLEAR
            </Button>

            <Button
              size="small"
              variant="contained"
              color="primary"
              type="submit"
              className={classes.searchButton}
            >
              SEARCH
            </Button>
          </CardActions>
        </form>
      </Paper>
    </div>
  );
};

export default AdvanceSearch;
